import type { InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface SwitchProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string;
  description?: string;
  /** Track colour when on: the brand pink, or the website's yellow (Link channel). */
  tone?: 'brand' | 'yellow';
}

/**
 * Controlled/uncontrolled toggle switch used for isActive flags and menu
 * visibility settings. Keeps a native checkbox underneath for a11y.
 */
export function Switch({
  label,
  description,
  className,
  id,
  disabled,
  tone = 'brand',
  ...rest
}: SwitchProps) {
  return (
    <label
      id={id}
      className={cn(
        'inline-flex items-center gap-3',
        disabled && 'cursor-not-allowed opacity-60',
        !disabled && 'cursor-pointer',
        className,
      )}
    >
      <span className="relative inline-block h-6 w-10 shrink-0">
        <input
          type="checkbox"
          disabled={disabled}
          className="peer sr-only"
          {...rest}
        />
        <span
          className={cn(
            'absolute inset-0 rounded-full bg-gray-300 transition-colors duration-150 dark:bg-gray-700',
            tone === 'yellow'
              ? 'peer-checked:bg-ferry-yellow dark:peer-checked:bg-ferry-yellow'
              : 'peer-checked:bg-brand-500 dark:peer-checked:bg-brand-500',
          )}
        />
        <span className="absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-150 peer-checked:translate-x-4" />
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && (
            <span className="block text-sm font-medium text-gray-900 dark:text-white">
              {label}
            </span>
          )}
          {description && (
            <span className="block text-xs text-gray-500 dark:text-gray-400">
              {description}
            </span>
          )}
        </span>
      )}
    </label>
  );
}
